// EmptyState — the "nothing here yet" primitive, pure.
//
// The calm atom shown where a list/canvas/panel has no content yet: an
// optional glyph, a title, optional supporting body, and an optional actions
// slot (children). Presentational only — it holds no state, does no I/O, and
// knows nothing of any domain; whatever the actions do is wired by the caller
// via the children they pass.
//
// Register (.claude/research/design/06): quiet and centred. No border and no fill at rest
// — grouping is done by whitespace, not an edge (§2/§5); an EmptyState sits on
// whatever ground it's dropped into. The body is fg-dim, the title is fg: the
// hierarchy is carried by weight + tone, not colour. The accent is NOT spent
// here — a primary action, if any, is composed as a child Button.
//
// Pure/props-driven: `icon`, `title`, `body`, and `children` are the whole
// surface. `title`/`body`/`icon` are ReactNode so the caller owns their text
// (and its i18n); this primitive contributes no visible strings of its own, so
// it needs no Lingui. The decorative glyph is marked aria-hidden here so the
// title carries the accessible meaning.

import type { ReactNode } from "react";
import "./EmptyState.css";

export type EmptyStateProps = {
    readonly icon?: ReactNode;
    readonly title: ReactNode;
    readonly body?: ReactNode;
    readonly children?: ReactNode;
    readonly className?: string;
};

export const EmptyState = ({
    icon,
    title,
    body,
    children,
    className,
}: EmptyStateProps) => (
    <div className={`bilda-empty-state${className ? ` ${className}` : ""}`}>
        {icon != null && (
            <span className="bilda-empty-state__icon" aria-hidden="true">
                {icon}
            </span>
        )}
        <p className="bilda-empty-state__title">{title}</p>
        {body != null && <p className="bilda-empty-state__body">{body}</p>}
        {children != null && (
            <div className="bilda-empty-state__actions">{children}</div>
        )}
    </div>
);
